/**
 * The sidebar badge's two counts, as a gql-only LEAF (PRO-130): the sidebar
 * imports this file, so it imports nothing but Apollo, React and generated
 * types — never a component, never `src/antd`.
 *
 * Two small queries, kept apart from the screens' own: "needs attention" and
 * the approval counts are cache singletons without an id, so a narrower
 * selection written under the same field would overwrite the page's fuller
 * one (and its items). `no-cache` keeps the badge out of the cache; it is
 * refreshed every 2 minutes, when the window regains focus, and by name from
 * the mutations that change the counts (`AUTOMATION_BADGE_QUERIES`).
 *
 * canView users: attention total + approvals for me + for my teams.
 * Approver-only users: the approvals part only. Nobody else asks.
 */
import { useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'

import type {
  AutomationSidebarApprovals,
  AutomationSidebarAttention,
} from './__generated__/types'
import { AutomationAccess, automationEntryFor } from './bootstrap'

export const AUTOMATION_SIDEBAR_ATTENTION = gql`
  query AutomationSidebarAttention {
    automationNeedsAttention {
      total
    }
  }
`

export const AUTOMATION_SIDEBAR_APPROVALS = gql`
  query AutomationSidebarApprovals {
    automationApprovalCounts {
      pendingForMe
      pendingForMyTeams
    }
  }
`

/** Operation names for `refetchQueries` in mutations that move the counts. */
export const AUTOMATION_BADGE_QUERIES = [
  'AutomationSidebarAttention',
  'AutomationSidebarApprovals',
]

export const AUTOMATION_BADGE_POLL_MS = 2 * 60 * 1000

const OPTIONS = {
  fetchPolicy: 'no-cache',
  pollInterval: AUTOMATION_BADGE_POLL_MS,
} as const

export const useAutomationBadge = (access: AutomationAccess): number => {
  const entry = automationEntryFor(access)
  const attention = useQuery<AutomationSidebarAttention>(
    AUTOMATION_SIDEBAR_ATTENTION,
    { ...OPTIONS, skip: entry !== 'module' }
  )
  const approvals = useQuery<AutomationSidebarApprovals>(
    AUTOMATION_SIDEBAR_APPROVALS,
    { ...OPTIONS, skip: entry === null }
  )

  const { refetch: refetchAttention } = attention
  const { refetch: refetchApprovals } = approvals
  useEffect(() => {
    if (entry === null) return undefined
    const onFocus = () => {
      if (entry === 'module') refetchAttention().catch(() => undefined)
      refetchApprovals().catch(() => undefined)
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [entry, refetchAttention, refetchApprovals])

  if (entry === null) return 0
  const counts = approvals.data?.automationApprovalCounts
  return (
    (entry === 'module'
      ? attention.data?.automationNeedsAttention.total ?? 0
      : 0) + (counts ? counts.pendingForMe + counts.pendingForMyTeams : 0)
  )
}
